import React from "react";
import { connect } from "react-redux";

import { HeadingWithIntro } from "../../../../../core/atoms/atoms";
import { SharedActions } from "../../../../../shared/flux/actions/sharedActions";
import { LocalStorageHelper } from "@travellocal/utils/localStorageHelper";
import { State as _State } from "../../../../../shared/state/reducers";
import { CustomerRequiredDetails } from "../../../../components/customer/customerRequiredDetails";
import { CustomerRequiredDetailsProgress } from "../../../../components/customer/customerRequiredDetailsProgress";
import { CustomerRequiredDetailsReview } from "../../../../components/customer/customerRequiredDetailsReview";
import { PaymentIframe } from "../../paymentIframe";

import "./makePayment.scss";

interface StateProps {
  lto: IState<ILto>;
  customerEnquiry: IState<IEnquiry>;
  customerEnquiryItinerary: IState<IEnquiryItinerarySummary>;
  cardTypes: IState<ICardType[]>;
}

interface LocalState {
  currentStage: number;
  isPaymentDialogOpen: boolean;
  reviewStageConfirmed: boolean;
  maxStages: number;
  inAddMode: boolean;
  inEditMode: boolean;
  travelerInEdit: ILeadTravelerDetails | ITravelerDetails | null;
}

class CustomerEnquiryMakePaymentComponent extends React.Component<StateProps, LocalState> {
  private iframe: PaymentIframe;

  public getRemainingBalance(): IPriceInCurrency {
    return {
      price: this.props.customerEnquiryItinerary.data.totalPrice.price - this.props.customerEnquiry.data.totalPaid.price,
      currency: this.props.customerEnquiryItinerary.data.totalPrice.currency,
    };
  }

  private findCurrentStage(maxStages: number): number {
    const {
      travelerDetails,
      leadTravelerDetails,
    } = this.props.customerEnquiry.data;

    const existingTravelerDetails = [ leadTravelerDetails, ...travelerDetails ];
    let currentStage;
    // Find first index with missing or insufficient details
    for (
      currentStage = 0;
      currentStage < maxStages && existingTravelerDetails[currentStage] && existingTravelerDetails[currentStage].isSufficientForBooking;
      ++currentStage
    ) {
      /* do nothing */
    }
    return currentStage;
  }

  private existingTravellers(): (ILeadTravelerDetails | ITravelerDetails)[] {
    const {
      leadTravelerDetails,
      travelerDetails,
    } = this.props.customerEnquiry.data;
    return [ leadTravelerDetails, ...travelerDetails ];
  }

  public componentWillMount() {
    const enquiry = this.props.customerEnquiry.data;

    const existingTravelerDetails = this.existingTravellers();
    const maxStages = enquiry.isConnected
      ? Math.max(existingTravelerDetails.length, enquiry.currentItinerarySummary.numberOfPeople)
      : existingTravelerDetails.length;

    const currentStage = this.findCurrentStage(maxStages);

    this.state = {
      currentStage,
      isPaymentDialogOpen: null,
      reviewStageConfirmed: false,
      maxStages,
      inAddMode: false,
      inEditMode: false,
      travelerInEdit: existingTravelerDetails[currentStage],
    };
  }

  private showReviewStage() {
    const {
      currentStage,
      maxStages,
      inEditMode,
      inAddMode,
    } = this.state;
    const {
      leadTravelerDetails,
      travelerDetails,
    } = this.props.customerEnquiry.data;

    if (inEditMode || inAddMode) {
      const mode = inEditMode ? "inEditMode" : "inAddMode";

      this.setState({
        [mode]: false,
        currentStage: maxStages,
      } as any);
    }

    return(
      <CustomerRequiredDetailsReview
        currentStage={currentStage}
        maxStages={maxStages}
        showPaymentStage={this.showPaymentStage.bind(this)}
        leadTravelerDetails={leadTravelerDetails}
        travelerDetails={travelerDetails}
        addTraveler={this.addTraveler.bind(this)}
        editTraveler={this.editTraveler.bind(this)}
      />
    );
  }

  public showPaymentStage() {
    if (!this.state.reviewStageConfirmed) {
      this.setState({ reviewStageConfirmed: true });
      this.makePayment();
    }
  }

  public makePayment() {
    const enquiry = this.props.customerEnquiry.data;
    if (!enquiry.isConnected) {
      return;
    }

    this.setState({ isPaymentDialogOpen: true }, () => {
      window.scroll(0, 0);

      this.iframe.initialize({
        remainingBalance: this.getRemainingBalance(),
        totalPaid: enquiry.totalPaid,
        type: SharedActions.Payment.Initialize,
        enquiryItinerary: this.props.customerEnquiryItinerary.data,
        enquiryStatus: enquiry.enquiryStatus,
        lto: enquiry.lto,
        authToken: LocalStorageHelper.getItem("authtoken"),
        cardTypes: this.props.cardTypes.data,
      });
    });
  }

  private allTravelerDetailsValid(maxStages: number): boolean {
    return (this.findCurrentStage(maxStages) === maxStages);
  }

  public checkRequiredTravellerDetails() {
    const {
      maxStages,
      inAddMode,
      inEditMode,
    } = this.state;

    const travelerDetailsValid = this.allTravelerDetailsValid(maxStages);

    if (travelerDetailsValid && inAddMode === false && inEditMode === false) {
      return true;
    } else {
      return false;
    }
  }

  private handleNextClicked = () => {
    const {
      currentStage,
      reviewStageConfirmed,
      inAddMode,
      inEditMode,
    } = this.state;

    if (!inAddMode) {
      const existingTravelerDetails = this.existingTravellers();
      const newCurrentStage = currentStage + 1;
      this.setState({
        currentStage: newCurrentStage,
        travelerInEdit: existingTravelerDetails[newCurrentStage],
      });
    }

    if (currentStage >= this.state.maxStages || inEditMode || inAddMode) {
      return this.showReviewStage();
    }

    if (reviewStageConfirmed) {
      this.makePayment();
    }
  }

  public addTraveler() {
    const maxStages = this.state.maxStages + 1;

    this.setState({
      maxStages,
      inAddMode: true,
    });
  }

  private cancelAddorEdit() {
    const {
      maxStages,
      inAddMode,
    } = this.state;

    const newMaxStages = inAddMode ? maxStages - 1 : maxStages;

    this.setState({
      currentStage: newMaxStages,
      maxStages: newMaxStages,
      inAddMode: false,
      inEditMode: false,
      travelerInEdit: null,
    });
  }

  private editTraveler(leadTraveler: boolean, key: number) {
    const {
      travelerDetails,
      leadTravelerDetails,
    } = this.props.customerEnquiry.data;

    let currentStage = 0;
    if (!leadTraveler) {
      currentStage = key + 1;
    }

    let travelerInEdit;
    if (leadTraveler) {
      travelerInEdit = leadTravelerDetails;
    } else {
      travelerInEdit = travelerDetails[key];
    }

    this.setState({
      currentStage,
      inEditMode: true,
      travelerInEdit,
    });
  }

  private renderForm = () => {
    const {
      currentStage,
      isPaymentDialogOpen,
      reviewStageConfirmed,
      maxStages,
      inAddMode,
      inEditMode,
      travelerInEdit,
    } = this.state;
    const hasRequiredTravellerDetails = this.checkRequiredTravellerDetails();

    if (!hasRequiredTravellerDetails) {
      const isLeadTraveler = currentStage === 0;

      return (
        <div className="stage">
          <CustomerRequiredDetailsProgress
            stage={currentStage}
            maxStages={maxStages}
            reviewStageConfirmed={reviewStageConfirmed}
            inEditMode={inEditMode}
          />
          <CustomerRequiredDetails
            key={currentStage}
            isLeadTraveler={isLeadTraveler}
            currentStage={currentStage}
            maxStages={maxStages}
            onNextClicked={this.handleNextClicked}
            inAddMode={inAddMode}
            cancelAddorEdit={this.cancelAddorEdit.bind(this)}
            inEditMode={inEditMode}
            travelerInEdit={travelerInEdit}
            showReviewStage={this.showReviewStage.bind(this)}
          />
        </div>
      );
    }

    if (currentStage >= maxStages && !isPaymentDialogOpen && reviewStageConfirmed === false) {
      return this.showReviewStage();
    }

    return (
      <div className="stage" key={currentStage}>
        <CustomerRequiredDetailsProgress
          stage={currentStage}
          maxStages={maxStages}
          reviewStageConfirmed={reviewStageConfirmed}
          inEditMode={inEditMode}
        />
        <HeadingWithIntro title="Make a new payment" description="" />
        <PaymentIframe ref={x => this.iframe = x} />
      </div>
    );
  }

  public render() {
    return (
      <div className="make-payment width-wrapper p-top-medium">
        {this.renderForm()}
      </div>
    );
  }
}

const stateToProps = (state: IWebStoreState): StateProps => {

  const enquiry = state.customerEnquiry.data;
  const itineraryState = enquiry.isConnected && enquiry.currentQuoteId
    ? state.customerItineraries.data[enquiry.currentQuoteId]
    : new _State<IEnquiryItinerarySummary>();

  return {
    lto: state.lto,
    customerEnquiry: state.customerEnquiry,
    customerEnquiryItinerary: itineraryState,
    cardTypes: state.cardTypes,
  };
};

export const MakePayment = connect(stateToProps)(CustomerEnquiryMakePaymentComponent);
